import React, { useState, useEffect } from "react";
import { ShieldCheck, ShieldX, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import DoneByBadge from "./DoneByBatch";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const KycSection = ({
  workerId,
  initialIsKycDone = false,
  onKycUpdate,
  kycDoneByName = null,
  kycDoneAt = null,
}) => {
  const [isKycDone, setIsKycDone] = useState(initialIsKycDone);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [localMeta, setLocalMeta] = useState({ byName: kycDoneByName, at: kycDoneAt });

  useEffect(() => {
    setIsKycDone(initialIsKycDone);
  }, [initialIsKycDone]);

  useEffect(() => {
    setLocalMeta({ byName: kycDoneByName, at: kycDoneAt });
  }, [kycDoneByName, kycDoneAt]);

  const handleMarkAsDone = async () => {
    if (isKycDone) return;
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/on-boarding/update-kyc`,
        { workerId, isKycDone: true },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      
      if (response.data.success) {
        setIsKycDone(true);
        setLocalMeta({ 
          byName: response.data.doneByName || null, 
          at: response.data.doneAt || new Date().toISOString()
        });
        setMessage(response.data.message || "KYC marked as done successfully.");
        setTimeout(() => setMessage(""), 3000);
        if (onKycUpdate) onKycUpdate(true);
      } else {
        throw new Error(response.data.message || "Failed to update KYC status");
      }
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || "Failed to update KYC status.");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`p-4 rounded-lg border transition-all ${
      isKycDone 
        ? "bg-green-50 border-green-300" 
        : "bg-gray-50 border-gray-300"
    }`}>
      {/* Single box content */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isKycDone ? "bg-green-100" : "bg-gray-200"
          }`}>
            {isKycDone ? (
              <ShieldCheck className={`h-5 w-5 ${isKycDone ? "text-green-600" : "text-gray-500"}`} />
            ) : (
              <ShieldX className="h-5 w-5 text-gray-500" />
            )}
          </div>
          
          {/* Text content */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${
                isKycDone ? "text-green-800" : "text-gray-700"
              }`}>
                KYC {isKycDone ? "Completed" : "Not Completed"}
              </span>
              {isKycDone && (
                <CheckCircle className="h-4 w-4 text-green-600" />
              )}
            </div>
            
            {/* Show metadata if available with better colors */}
            {(localMeta.byName || localMeta.at) && (
              <div className="mt-2">
                <DoneByBadge 
                  doneByName={localMeta.byName} 
                  doneAt={localMeta.at} 
                  label={isKycDone ? "Completed" : "Updated"}
                  variant={isKycDone ? "success" : "default"}
                />
              </div>
            )}
            
            {/* Success/Error message */}
            {message && (
              <p className={`text-xs mt-2 ${
                message.includes("success") || message.includes("done") 
                  ? "text-green-600" 
                  : "text-red-600"
              }`}>
                {message}
              </p>
            )}
          </div>
        </div>
        
        {/* Action button - only show when KYC is not done */}
        {!isKycDone && (
          <button
            onClick={handleMarkAsDone}
            disabled={isSubmitting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-all shadow-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed ml-4 flex-shrink-0"
          >
            {isSubmitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Marking...</>
            ) : (
              <>Mark as Done</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default KycSection;